"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { ChevronRight, Info, Facebook, Instagram, Music } from "lucide-react";
function SocialLinksConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  if (loading) {
    return <div className="flex justify-center space-x-4 animate-pulse">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="size-12 bg-muted rounded-lg" />)}
      </div>;
  }
  if (posts.length === 0) return null;
  return <div className="flex flex-wrap justify-center gap-4">
      {posts.map((post, i) => {
      const platform = post.customFields?.platform?.toLowerCase() || "";
      const url = post.customFields?.social_url || "#";
      let Icon = Info;
      if (platform.includes("facebook")) Icon = Facebook;
      if (platform.includes("instagram")) Icon = Instagram;
      if (platform.includes("tiktok")) Icon = Music;
      return <Link key={post.id} to={url} newTab data-index={i}>
            <Button variant="default" size="icon" className="size-12 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground shadow-md transition-transform hover:scale-110">
              <Icon className="size-6" />
              <span className="sr-only">{post.title}</span>
            </Button>
          </Link>;
    })}
    </div>;
}
function PostListConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="space-y-12">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="animate-pulse">
            <div className="h-8 bg-muted/50 rounded-md w-3/4 mb-6"></div>
            <div className="h-10 bg-muted/50 rounded-md w-40 mb-6"></div>
            <div className="h-64 bg-muted/50 rounded-md w-full mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-muted/50 rounded-md w-full"></div>
              <div className="h-4 bg-muted/50 rounded-md w-5/6"></div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="p-6 bg-destructive/10 border border-destructive/20 rounded-md text-destructive">
        <p className="font-default font-semibold">No se pudieron cargar las noticias.</p>
        <p className="text-sm mt-2 opacity-80">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="p-12 text-center border border-border/50 bg-card/20 backdrop-blur-sm rounded-md">
        <span className="size-8 mx-auto text-muted-foreground mb-4 flex items-center justify-center"><Info className="size-8" /></span>
        <p className="text-lg text-muted-foreground font-default">No hay noticias recientes para mostrar.</p>
      </div>;
  }
  return <div className="space-y-16">
      {posts.slice(0, 6).map((post, i) => {
      const carouselImages = post.featuredImage ? [post.featuredImage] : [];
      return <article key={post.id} data-index={i} className="pb-16 border-b border-border/40 last:border-0 last:pb-0">
            <h3 className="text-[28px] leading-tight font-bold text-foreground font-default mb-6 tracking-tight hover:text-primary hover:translate-x-1 transition-all duration-300 cursor-pointer">
              {post.title}
            </h3>

            {carouselImages.length > 0 && <div className="mb-6">
                <Carousel className="w-full relative group/carousel">
                  <CarouselContent>
                    {carouselImages.map((imgSrc, imgIndex) => <CarouselItem key={imgIndex} data-index={imgIndex}>
                        <div className="relative aspect-video overflow-hidden rounded-md bg-muted/20 border border-border/30">
                          <img src={imgSrc} alt={`Imagen de noticia ${imgIndex + 1} for ${post.title}`} className="w-full h-full object-cover transition-transform duration-700 group-hover/carousel:scale-105" loading="lazy" decoding="async" />
                          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent opacity-60" />
                        </div>
                      </CarouselItem>)}
                  </CarouselContent>
                </Carousel>
              </div>}

            <div className="text-chart-3 font-default leading-relaxed mb-6 line-clamp-3 text-lg" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />

            <Link to={post.link || "#"} className="block w-full">
              <Button size="lg" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-lg py-7 rounded-md uppercase tracking-wider shadow-md transition-all">
                Leer Noticia Completa
                <ChevronRight className="ml-2 size-5" />
              </Button>
            </Link>
          </article>;
    })}
    </div>;
}
export default function ProjectMilestones() {
  const social_query = new WP_Query({
    post_type: "social_media_link",
    per_page: 10
  });
  return <section data-section-id="233685" id="noticias-recientes" className="bg-background text-foreground py-16 md:py-24 relative">
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672556/img/news_paper_texture.png)] mix-blend-overlay" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          <div className="lg:col-span-8">
            <div className="mb-12 border-l-4 border-primary pl-6">
              <h2 className="text-3xl md:text-4xl font-bold font-default tracking-tight uppercase text-foreground">
                Noticias <span className="text-primary">Recientes</span>
              </h2>
              <p className="text-chart-3 mt-2 font-sans max-w-2xl">
                La actualidad del suroeste de Florida, minuto a minuto con 239 Ahora.
              </p>
            </div>
            
            <PostsProvider wp_query={{
            post_type: "post",
            per_page: 6,
            offset: 1,
            order: "desc",
            orderby: "date"
          }}>
              <PostListConsumer />
            </PostsProvider>
          </div>

          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-8">
              
              <div className="bg-card text-card-foreground p-6 rounded-md border-t-4 border-primary shadow-lg">
                <h3 className="text-xl font-bold font-default mb-4 flex items-center">
                  <span className="mr-2 size-5 text-primary flex items-center justify-center"><Info className="size-5" /></span>
                  Sobre 239 Ahora
                </h3>
                <ul className="space-y-3 font-sans text-sm opacity-90">
                  <li className="flex justify-between border-b border-border pb-2">
                    <span className="font-semibold">Región:</span>
                    <span className="text-right">Suroeste de Florida</span>
                  </li>
                  <li className="flex justify-between border-b border-border pb-2">
                    <span className="font-semibold">Cobertura:</span>
                    <span className="text-right">Local y Estatal</span>
                  </li>
                  <li className="flex justify-between pb-1">
                    <span className="font-semibold">Enfoque:</span>
                    <span className="text-right">Periodismo Independiente</span>
                  </li>
                </ul>
              </div>

              

            </div>
          </div>

        </div>
      </div>
    </section>;
}